import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2,
  Users,
  Shield,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  UserPlus,
  UserMinus,
  Settings,
  ExternalLink,
  Trash2,
  Search,
  RefreshCw,
  Ban,
  CheckCircle,
  AlertTriangle,
  Archive,
  RotateCcw,
  X,
  CreditCard,
  Calendar,
  Clock,
  Phone,
  Globe,
  Zap,
  BookOpen,
  Mic,
  PenLine,
  Headphones
} from 'lucide-react';
import { toast } from 'sonner';
import { usersAPI, authAPI, settingsAPI } from '../services/api';
import { Wifi, WifiOff } from 'lucide-react';

const COMPETENCES = [
  { key: 'comprehension_orale',  label: 'Compréhension Orale',  icon: Headphones, color: 'text-blue-500',   bg: 'bg-blue-500/10' },
  { key: 'comprehension_ecrite', label: 'Compréhension Écrite', icon: BookOpen,   color: 'text-green-500', bg: 'bg-green-500/10' },
  { key: 'expression_ecrite',    label: 'Expression Écrite',    icon: PenLine,    color: 'text-purple-500',bg: 'bg-purple-500/10' },
  { key: 'expression_orale',     label: 'Expression Orale',     icon: Mic,        color: 'text-orange-500',bg: 'bg-orange-500/10' },
];

function UserDetailModal({ user, isOnline, packs, onClose }) {
  const today = new Date();

  const debutDate  = user.debut_abonnement ? new Date(user.debut_abonnement) : null;
  const finDate    = user.fin_abonnement   ? new Date(user.fin_abonnement)   : null;
  const isActive   = finDate && finDate > today;
  const daysLeft   = finDate ? Math.max(0, Math.ceil((finDate - today) / 86400000)) : 0;
  const totalDays  = (debutDate && finDate) ? Math.ceil((finDate - debutDate) / 86400000) : null;

  const [effectiveFreeBlocs, setEffectiveFreeBlocs] = useState(null);
  const [loadingAccess, setLoadingAccess] = useState(true);

  const fmt = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const loadBlocs = async () => {
      try {
        if (user.center_id && user.type_pack) {
          // Center user: fetch the admin's center packs and find the one matching the user's pack
          const centerPacks = await settingsAPI.getCenterPacks(user.center_id);
          const matchedPack = Array.isArray(centerPacks)
            ? centerPacks.find(p => p.name?.toLowerCase() === user.type_pack?.toLowerCase())
            : null;
          if (matchedPack?.blocs_limits) {
            const bl = matchedPack.blocs_limits;
            setEffectiveFreeBlocs({
              comprehension_orale:  bl.comprehension_orale  === -1 ? 9999 : (bl.comprehension_orale  ?? 1),
              comprehension_ecrite: bl.comprehension_ecrite === -1 ? 9999 : (bl.comprehension_ecrite ?? 1),
              expression_ecrite:    bl.expression_ecrite    === -1 ? 9999 : (bl.expression_ecrite    ?? 1),
              expression_orale:     bl.expression_orale     === -1 ? 9999 : (bl.expression_orale     ?? 1),
            });
            return;
          }
          // Pack not found — fall back to center free_blocs
          const cfg = await settingsAPI.getCenterAccessConfig(user.center_id);
          if (cfg?.free_blocs) setEffectiveFreeBlocs(cfg.free_blocs);
        } else {
          // Non-center user: use global effective config
          const cfg = await settingsAPI.getEffectiveAccessConfig(user.id);
          if (cfg?.free_blocs) {
            let resolved = { ...cfg.free_blocs };
            if (isActive && user.type_pack) {
              const matchedPack = packs.find(p => p.name?.toLowerCase() === user.type_pack?.toLowerCase());
              if (matchedPack?.blocs_limits) {
                COMPETENCES.forEach(({ key }) => {
                  const limit = matchedPack.blocs_limits[key];
                  if (limit != null) resolved[key] = limit === -1 ? 9999 : limit;
                });
              }
            }
            setEffectiveFreeBlocs(resolved);
          }
        }
      } catch (e) {
        // leave null
      } finally {
        setLoadingAccess(false);
      }
    };
    loadBlocs();
  }, [user.id, user.center_id, user.type_pack, isActive, packs]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-card rounded-2xl border shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center space-x-3">
            <div className={`relative h-12 w-12 rounded-full flex items-center justify-center text-lg font-bold ${isOnline ? 'bg-green-500/20 text-green-600' : 'bg-primary/20 text-primary'}`}>
              {user.nom?.charAt(0) || user.username?.charAt(0) || '?'}
              <div className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-card ${isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
            </div>
            <div>
              <h3 className="text-lg font-bold leading-tight">{user.nom || user.username}</h3>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {isOnline && <span className="text-xs bg-green-500/20 text-green-600 px-2 py-1 rounded-full font-medium">En ligne</span>}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-secondary transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Abonnement */}
          <section>
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Abonnement</h4>
            <div className="bg-secondary/50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Pack</span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  user.type_pack === 'Premium' || user.type_pack === 'VIP' ? 'bg-primary/20 text-primary' :
                  user.type_pack === 'Standard' ? 'bg-green-500/20 text-green-600' :
                  'bg-secondary text-secondary-foreground border'
                }`}>
                  {user.type_pack || 'Gratuit'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />Début</span>
                <span className="text-sm font-medium">{fmt(user.debut_abonnement)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />Fin</span>
                <span className={`text-sm font-medium ${isActive ? 'text-foreground' : 'text-destructive'}`}>{fmt(user.fin_abonnement)}</span>
              </div>
              {totalDays !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />Durée totale</span>
                  <span className="text-sm font-medium">{totalDays} jour{totalDays !== 1 ? 's' : ''}</span>
                </div>
              )}
              {finDate && (
                <div className="flex items-center justify-between border-t pt-3">
                  <span className="text-sm font-medium">Statut</span>
                  {isActive ? (
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      Actif — {daysLeft} jour{daysLeft !== 1 ? 's' : ''} restant{daysLeft !== 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-destructive">
                      <AlertTriangle className="h-4 w-4" />
                      Expiré
                    </span>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Essais IA */}
          <section>
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Essais IA</h4>
            <div className="bg-secondary/50 rounded-xl p-4 flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-muted-foreground"><Zap className="h-4 w-4 text-yellow-500" />Crédits disponibles</span>
              <span className={`text-2xl font-bold ${user.credits_ia > 0 ? 'text-foreground' : 'text-destructive'}`}>
                {user.credits_ia ?? 0}
              </span>
            </div>
          </section>

          {/* Accès par compétence */}
          <section>
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Accès par compétence</h4>
            <div className="grid grid-cols-2 gap-2">
              {COMPETENCES.map(({ key, label, icon: Icon, color, bg }) => {
                const count = effectiveFreeBlocs ? effectiveFreeBlocs[key] : null;
                const display = count === 9999 ? '∞' : count;
                return (
                  <div key={key} className={`${bg} rounded-xl p-3 flex items-center gap-3`}>
                    <div className="p-2 bg-white/20 dark:bg-black/10 rounded-lg">
                      <Icon className={`h-4 w-4 ${color}`} />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground leading-tight">{label}</p>
                      {loadingAccess ? (
                        <div className="h-5 w-6 bg-muted-foreground/20 animate-pulse rounded mt-1" />
                      ) : (
                        <p className={`text-lg font-bold ${color}`}>
                          {display !== null ? display : <span className="text-sm font-normal text-muted-foreground">—</span>}
                        </p>
                      )}
                      {!loadingAccess && count !== null && count !== 9999 && (
                        <p className="text-xs text-muted-foreground -mt-0.5">bloc{count !== 1 ? 's' : ''}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Infos contact */}
          <section>
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Informations</h4>
            <div className="bg-secondary/50 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />Téléphone</span>
                <span className="text-sm font-medium">{user.telephone || '—'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" />Pays</span>
                <span className="text-sm font-medium">{user.pays || '—'}</span>
              </div>
              {user.created_at && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />Inscrit le</span>
                  <span className="text-sm font-medium">{fmt(user.created_at)}</span>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export function CenterManager() {
  const [groupedData, setGroupedData] = useState({ centers: [], unassigned: [] });
  const [allUsers, setAllUsers] = useState([]);
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [expandedCenters, setExpandedCenters] = useState({});
  const [copiedCode, setCopiedCode] = useState(null);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUserDetail, setSelectedUserDetail] = useState(null);
  const [packs, setPacks] = useState([]);

  useEffect(() => {
    loadData();
    settingsAPI.getPacks().then(data => setPacks(Array.isArray(data) ? data : [])).catch(() => {});

    // Refresh sessions every 30 seconds
    const interval = setInterval(() => {
      loadSessions();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadSessions = async () => {
    try {
      // Load ALL active sessions (including admins)
      const sessions = await authAPI.getAllActiveSessions();
      setAllSessions(Array.isArray(sessions) ? sessions : []);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const grouped = await usersAPI.getGroupedByCenter();
      setGroupedData(grouped);
      
      // Get ALL users (not admins) for potential promotion
      const allRegularUsers = await usersAPI.getAll();
      
      // Filter to only show regular users (not admins, not super_admin)
      const eligibleForPromotion = (Array.isArray(allRegularUsers) ? allRegularUsers : []).filter(u => 
        u.role === 'user' && !u.is_archived
      );
      setAllUsers(eligibleForPromotion);
      
      // Load archived users
      const archived = await usersAPI.getArchivedUsers();
      setArchivedUsers(Array.isArray(archived) ? archived : []);
      
      // Load ALL active sessions
      const sessions = await authAPI.getAllActiveSessions();
      setAllSessions(Array.isArray(sessions) ? sessions : []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Check if user is online
  const isUserOnline = (userId) => {
    return allSessions.some(session => 
      session.user_id === userId && !!session.is_active
    );
  };

  const toggleCenterExpand = (centerId) => {
    setExpandedCenters(prev => ({
      ...prev,
      [centerId]: !prev[centerId]
    }));
  };

  // Fonction utilitaire pour copier avec fallback
  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    } catch (err) {
      console.error('Erreur de copie:', err);
      return false;
    }
  };

  const handleCopyCode = async (code) => {
    const success = await copyToClipboard(code);
    if (success) {
      setCopiedCode(code);
      toast.success('Code copié!');
      setTimeout(() => setCopiedCode(null), 2000);
    } else {
      toast.error('Impossible de copier. Code: ' + code);
    }
  };

  const handleCopyInviteLink = async (code) => {
    const link = `${window.location.origin}/?code=${code}`;
    const success = await copyToClipboard(link);
    if (success) {
      toast.success('Lien d\'invitation copié!');
    } else {
      toast.error('Impossible de copier. Lien: ' + link);
    }
  };

  const handlePromoteUser = async (userId) => {
    try {
      const result = await usersAPI.promoteToAdmin(userId);
      if (result.success) {
        toast.success(`Utilisateur promu administrateur! Code: ${result.data?.invitation_code || 'Généré'}`);
        loadData();
        setShowPromoteModal(false);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Erreur lors de la promotion');
    }
  };

  const handleDemoteAdmin = async (userId, adminName) => {
    if (!window.confirm(`Rétrograder ${adminName} en utilisateur simple? Cette action est irréversible.`)) {
      return;
    }
    
    try {
      const result = await usersAPI.demoteFromAdmin(userId);
      if (result.success) {
        toast.success('Administrateur rétrogradé en utilisateur');
        loadData();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Erreur lors de la rétrogradation');
    }
  };

  const handleToggleCenterBlock = async (adminId, adminName, currentlyBlocked) => {
    const action = currentlyBlocked ? 'débloquer' : 'bloquer';
    const confirmMsg = currentlyBlocked 
      ? `Débloquer le centre "${adminName}"? L'admin et tous ses utilisateurs pourront se reconnecter.`
      : `Bloquer le centre "${adminName}"? L'admin et tous ses utilisateurs ne pourront plus se connecter.`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    try {
      const result = await usersAPI.toggleCenterBlock(adminId);
      if (result.success) {
        toast.success(result.message);
        loadData();
      } else {
        toast.error(result.message || `Erreur lors du ${action}`);
      }
    } catch (error) {
      toast.error(`Erreur lors du ${action} du centre`);
    }
  };

  const handleAssignUser = async (userId) => {
    if (!selectedCenter) return;
    
    try {
      const result = await usersAPI.assignToCenter(userId, selectedCenter.admin.id);
      if (result.success) {
        toast.success('Utilisateur assigné au centre');
        loadData();
        setShowAssignModal(false);
        setSelectedCenter(null);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'assignation');
    }
  };

  const handleArchiveUser = async (userId, userName) => {
    if (!window.confirm(`Archiver l'utilisateur "${userName}"?`)) {
      return;
    }
    
    try {
      const result = await usersAPI.archive(userId);
      if (result.success) {
        toast.success('Utilisateur archivé');
        loadData();
      } else {
        toast.error(result.message || 'Erreur lors de l\'archivage');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleRestoreUser = async (userId, userName) => {
    try {
      const result = await usersAPI.restore(userId);
      if (result.success) {
        toast.success(`${userName} a été réactivé`);
        loadData();
      } else {
        toast.error(result.message || 'Erreur lors de la réactivation');
      }
    } catch (error) {
      toast.error('Erreur lors de la réactivation');
    }
  };

  const handleDeleteUserPermanently = async (userId, userName) => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT l'utilisateur "${userName}"? Cette action est irréversible!`)) {
      return;
    }
    
    try {
      const result = await usersAPI.deleteUser(userId);
      if (result.success) {
        toast.success('Utilisateur supprimé définitivement');
        loadData();
      } else {
        toast.error(result.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleRemoveFromCenter = async (userId, userName) => {
    if (!window.confirm(`Retirer ${userName} de ce centre?`)) {
      return;
    }
    
    try {
      const result = await usersAPI.removeFromCenter(userId);
      if (result.success) {
        toast.success('Utilisateur retiré du centre');
        loadData();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Erreur lors du retrait');
    }
  };

  const filteredUsers = allUsers.filter(user => 
    user.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalUsers = groupedData.centers.reduce((sum, c) => sum + (c.user_count || 0), 0) + groupedData.unassigned.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center space-x-2">
            <Building2 className="h-6 w-6 text-primary" />
            <span>Gestion des Centres Linguistiques</span>
          </h2>
          <p className="text-muted-foreground">
            Gérez les administrateurs de centres et leurs utilisateurs
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={loadData}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg border hover:bg-secondary transition-colors"
            title="Actualiser"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Actualiser</span>
          </button>
          <button
            onClick={() => setShowPromoteModal(true)}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            data-testid="create-center-btn"
          >
            <UserPlus className="h-4 w-4" />
            <span>Créer un Centre</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card p-4 rounded-xl border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Building2 className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Centres actifs</p>
              <p className="text-2xl font-bold">{groupedData.centers.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-card p-4 rounded-xl border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <Users className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total utilisateurs</p>
              <p className="text-2xl font-bold">{totalUsers}</p>
            </div>
          </div>
        </div>
        <div className="bg-card p-4 rounded-xl border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-500/10 rounded-lg">
              <UserMinus className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Sans centre</p>
              <p className="text-2xl font-bold">{groupedData.unassigned.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Centers List */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Centres Linguistiques</h3>
        
        {groupedData.centers.length === 0 ? (
          <div className="bg-card p-8 rounded-xl border text-center">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">Aucun centre créé</p>
            <p className="text-sm text-muted-foreground mt-1">
              Cliquez sur "Créer un Centre" pour promouvoir un utilisateur en administrateur
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {groupedData.centers.map((center) => {
              const adminOnline = isUserOnline(center.admin.id);
              return (
              <div 
                key={center.admin.id} 
                className={`bg-card rounded-xl border overflow-hidden ${center.admin.is_blocked ? 'border-red-500/50 bg-red-500/5' : ''}`}
              >
                {/* Blocked Banner */}
                {center.admin.is_blocked && (
                  <div className="bg-red-500/10 border-b border-red-500/20 px-4 py-2 flex items-center space-x-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-500 font-medium">Centre bloqué - Paiement en attente</span>
                  </div>
                )}
                
                {/* Center Header */}
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-secondary/50 transition-colors"
                  onClick={() => toggleCenterExpand(center.admin.id)}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`relative p-2 rounded-lg ${center.admin.is_blocked ? 'bg-red-500/10' : adminOnline ? 'bg-green-500/10' : 'bg-primary/10'}`}>
                      <Shield className={`h-6 w-6 ${center.admin.is_blocked ? 'text-red-500' : adminOnline ? 'text-green-500' : 'text-primary'}`} />
                      {/* Online indicator for admin */}
                      <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white dark:border-gray-900 ${adminOnline ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h4 className="font-semibold">{center.settings?.siteName || center.admin.nom || center.admin.username}</h4>
                        {adminOnline && (
                          <span className="text-xs bg-green-500/20 text-green-600 px-2 py-0.5 rounded-full">En ligne</span>
                        )}
                        {center.admin.is_blocked && (
                          <span className="text-xs bg-red-500/20 text-red-500 px-2 py-0.5 rounded-full">Bloqué</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Admin: {center.admin.nom || center.admin.username} • {center.user_count || 0} utilisateur(s)
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 items-center">
                    {/* Block/Unblock Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleCenterBlock(
                          center.admin.id,
                          center.settings?.siteName || center.admin.nom || center.admin.username,
                          center.admin.is_blocked
                        );
                      }}
                      className={`p-2 rounded-lg transition-colors ${
                        center.admin.is_blocked
                          ? 'bg-green-500/10 hover:bg-green-500/20 text-green-500'
                          : 'bg-red-500/10 hover:bg-red-500/20 text-red-500'
                      }`}
                      title={center.admin.is_blocked ? 'Débloquer le centre' : 'Bloquer le centre'}
                      data-testid={`toggle-block-${center.admin.id}`}
                    >
                      {center.admin.is_blocked ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <Ban className="h-4 w-4" />
                      )}
                    </button>

                    {/* Invitation Code */}
                    <div className="flex items-center space-x-2 bg-secondary px-3 py-1.5 rounded-lg">
                      <span className="text-xs text-muted-foreground hidden sm:inline">Code:</span>
                      <span className="font-mono font-bold text-sm">{center.admin.invitation_code}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyCode(center.admin.invitation_code);
                        }}
                        className="p-1 hover:bg-background rounded"
                        data-testid={`copy-code-${center.admin.invitation_code}`}
                      >
                        {copiedCode === center.admin.invitation_code ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                    </div>

                    {/* Copy Link Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyInviteLink(center.admin.invitation_code);
                      }}
                      className="p-2 hover:bg-secondary rounded-lg transition-colors"
                      title="Copier le lien d'invitation"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </button>

                    {/* Expand/Collapse */}
                    {expandedCenters[center.admin.id] ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </div>
                
                {/* Expanded Content */}
                {expandedCenters[center.admin.id] && (
                  <div className="border-t px-4 py-3 bg-secondary/30">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium">Utilisateurs du centre</span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedCenter(center);
                            setShowAssignModal(true);
                          }}
                          className="text-xs px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors flex items-center space-x-1"
                        >
                          <UserPlus className="h-3 w-3" />
                          <span>Assigner un utilisateur</span>
                        </button>
                        <button
                          onClick={() => handleDemoteAdmin(center.admin.id, center.admin.nom || center.admin.username)}
                          className="text-xs px-3 py-1.5 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors flex items-center space-x-1"
                        >
                          <Trash2 className="h-3 w-3" />
                          <span>Supprimer le centre</span>
                        </button>
                      </div>
                    </div>
                    
                    {center.users.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-2">Aucun utilisateur inscrit</p>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {center.users.map((user) => {
                          const online = isUserOnline(user.id);
                          return (
                            <div
                              key={user.id}
                              className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${online ? 'bg-green-500/10 hover:bg-green-500/20' : 'bg-card hover:bg-secondary/70'}`}
                              onClick={() => setSelectedUserDetail(user)}
                            >
                              <div className="flex items-center space-x-3">
                                <div className="relative">
                                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium ${online ? 'bg-green-500/20 text-green-600' : 'bg-primary/20'}`}>
                                    {user.nom?.charAt(0) || user.username?.charAt(0) || '?'}
                                  </div>
                                  <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white dark:border-gray-900 ${online ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                                </div>
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <p className="text-sm font-medium">{user.nom || user.username}</p>
                                    {online && <span className="text-xs text-green-600 font-medium">En ligne</span>}
                                  </div>
                                  <p className="text-xs text-muted-foreground">{user.email}</p>
                                </div>
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRemoveFromCenter(user.id, user.nom || user.username); }}
                                className="p-1.5 hover:bg-red-500/10 rounded text-red-500"
                                title="Retirer du centre"
                              >
                                <UserMinus className="h-4 w-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
            })}
          </div>
        )}
      </div>

      {/* Archived Users */}
      {archivedUsers.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center space-x-2">
            <Archive className="h-5 w-5 text-red-500" />
            <span>Utilisateurs archivés ({archivedUsers.length})</span>
          </h3>
          <div className="bg-card rounded-xl border p-4 border-dashed border-red-300">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {archivedUsers.map((user) => (
                <div 
                  key={user.id}
                  className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-950/20 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 rounded-full bg-red-500/20 flex items-center justify-center text-sm font-medium text-red-600">
                      {user.nom?.charAt(0) || user.username?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-red-600 dark:text-red-400">{user.nom || user.username}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                      {user.archived_reason && (
                        <p className="text-xs text-red-500 mt-0.5">{user.archived_reason}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleRestoreUser(user.id, user.nom || user.username)}
                      className="p-2 rounded-lg bg-green-500/10 hover:bg-green-500/20 text-green-600 transition-colors"
                      title="Réactiver l'utilisateur"
                      data-testid={`restore-user-${user.id}`}
                    >
                      <RotateCcw className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteUserPermanently(user.id, user.nom || user.username)}
                      className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-500 transition-colors"
                      title="Supprimer définitivement"
                      data-testid={`delete-user-${user.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Unassigned Users Section */}
      {groupedData.unassigned.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center space-x-2">
            <UserMinus className="h-5 w-5 text-orange-500" />
            <span>Utilisateurs sans centre ({groupedData.unassigned.length})</span>
          </h3>
          <div className="bg-card rounded-xl border p-4">
            <p className="text-sm text-muted-foreground mb-4">
              Ces utilisateurs ne sont assignés à aucun centre linguistique. Ils voient les paramètres globaux du SuperAdmin.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {groupedData.unassigned.map((user) => {
                const isOnline = isUserOnline(user.id);
                return (
                  <div 
                    key={user.id}
                    className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <div className="h-10 w-10 rounded-full bg-orange-500/20 flex items-center justify-center text-sm font-medium text-orange-600">
                          {user.nom?.charAt(0) || user.username?.charAt(0) || '?'}
                        </div>
                        <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white dark:border-gray-900 ${isOnline ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                      </div>
                      <div>
                        <p className="text-sm font-medium">{user.nom || user.username}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleArchiveUser(user.id, user.nom || user.username)}
                        className="p-2 rounded-lg bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-600 transition-colors"
                        title="Archiver"
                        data-testid={`archive-unassigned-${user.id}`}
                      >
                        <Archive className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUserPermanently(user.id, user.nom || user.username)}
                        className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-500 transition-colors"
                        title="Supprimer"
                        data-testid={`delete-unassigned-${user.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {selectedUserDetail && (
        <UserDetailModal
          user={selectedUserDetail}
          isOnline={isUserOnline(selectedUserDetail.id)}
          packs={packs}
          onClose={() => setSelectedUserDetail(null)}
        />
      )}

      {/* Promote User Modal */}
      {showPromoteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl max-w-md w-full max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold flex items-center space-x-2">
                <UserPlus className="h-5 w-5 text-primary" />
                <span>Créer un nouveau centre</span>
              </h3>
              <button
                onClick={() => setShowPromoteModal(false)}
                className="p-2 hover:bg-secondary rounded-lg"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-muted-foreground mb-4">
                Sélectionnez un utilisateur à promouvoir en administrateur de centre linguistique.
                Un code d'invitation unique sera généré.
              </p>
              
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher un utilisateur..."
                  className="w-full pl-10 pr-4 py-2 rounded-lg border bg-background"
                />
              </div>
              
              <div className="max-h-60 overflow-y-auto space-y-2">
                {filteredUsers.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    Aucun utilisateur disponible
                  </p>
                ) : (
                  filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handlePromoteUser(user.id)}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-secondary transition-colors text-left"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center font-medium">
                          {user.nom?.charAt(0) || user.username?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium">{user.nom || user.username}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                      <Shield className="h-5 w-5 text-primary" />
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign User to Center Modal */}
      {showAssignModal && selectedCenter && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl max-w-md w-full max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold flex items-center space-x-2">
                <UserPlus className="h-5 w-5 text-primary" />
                <span>Assigner au centre: {selectedCenter.settings?.siteName || selectedCenter.admin.nom}</span>
              </h3>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedCenter(null);
                }}
                className="p-2 hover:bg-secondary rounded-lg"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-muted-foreground mb-4">
                Sélectionnez un utilisateur à assigner à ce centre.
              </p>
              
              <div className="max-h-60 overflow-y-auto space-y-2">
                {groupedData.unassigned.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    Aucun utilisateur disponible
                  </p>
                ) : (
                  groupedData.unassigned.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleAssignUser(user.id)}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-secondary transition-colors text-left"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center font-medium">
                          {user.nom?.charAt(0) || user.username?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium">{user.nom || user.username}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                      <UserPlus className="h-5 w-5 text-green-500" />
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
